/**
 * Telling a bot wall apart from a product page.
 *
 * A wall is not an HTTP failure. Amazon's — the "Click the button below to
 * continue shopping" interstitial — arrives as **200 OK** carrying four
 * kilobytes of markup whose `<title>` is `Amazon.com`, and it appears on a
 * fraction of requests rather than all of them. Nothing upstream of here can
 * see it: the fetch succeeded, the parse succeeded, and the extractors then
 * dutifully report the only title the page had.
 *
 * Left undetected that becomes the worst outcome the pipeline has — an item
 * that says "Imported" and is named after the shop, silently overwriting
 * whatever was there before. So it's checked for by name, and the run fails as
 * blocked, which is the state the panel already offers "Paste Page Instead"
 * for.
 *
 * Markers, not heuristics. "This page is small and has no product on it" also
 * describes a legitimately sparse listing, and refusing to import one of those
 * would be a worse bug than the one being fixed.
 */

const MARKERS: readonly RegExp[] = [
  // Amazon. The form action is the strongest signal — it's on both the
  // "continue shopping" interstitial and the older character-entry page — and
  // the comment addressed to scrapers rides on every variant of both.
  /\/errors_page\/validateCaptcha/i,
  /to discuss automated access to amazon data/i,
  /enter the characters you see below/i,

  // Cloudflare's interstitial and block page.
  /<title>just a moment/i,
  /attention required!\s*\|\s*cloudflare/i,
  /cf-browser-verification|cf_chl_opt/i,

  // Akamai and PerimeterX, which sit in front of a good share of retail.
  /reference #\d+\.[0-9a-f]+\.\d+\.[0-9a-f]+/i,
  /please verify you are a human|px-captcha/i,

  // DataDome.
  /captcha-delivery\.com/i,
];

/**
 * Whether the shop served a challenge instead of the page.
 *
 * Only ever asked of a page the app fetched itself. Markup the user pasted out
 * of their own browser is by definition past the wall, and a false positive
 * there would send them back to a dialog they just came from.
 */
export function isBotWall(html: string): boolean {
  // The interstitials are all small; a product page that happens to mention
  // one of these strings in a review or a script bundle is not a wall.
  if (html.length > 100_000) return false;

  return MARKERS.some((marker) => marker.test(html));
}
